import * as React from "react";
import { FormControl, Grid, InputLabel, OutlinedInput } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { CustomField, useCreateCustomField, useUpdateCustomField } from "@/hooks/api/use-campaigns";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";

export function CreateCustomField({
	campaignId,
	customField,
	isCreateDrawerOpen,
	onCreateDrawerClose,
}: {
	campaignId: string;
	customField: CustomField | null;
	isCreateDrawerOpen: boolean;
	onCreateDrawerClose: () => void;
}): React.JSX.Element {
	const createCustomFieldMutation = useCreateCustomField();
	const updateCustomFieldMutation = useUpdateCustomField();

	const isEdit = !!customField?.id;

	const methods = useForm<any>({
		defaultValues: {
			name: customField?.name || "",
			label: customField?.label || "",
			description: customField?.description || "",
			enabled: customField?.enabled || false,
		},
	});

	const {
		handleSubmit,
		reset,
		control,
		setError,
		formState: { isSubmitting },
	} = methods;

	React.useLayoutEffect(() => {
		reset({
			name: customField?.name || "",
			label: customField?.label || "",
			description: customField?.description || "",
			enabled: customField?.enabled || false,
		});
	}, [customField, reset, isCreateDrawerOpen]);

	const onSubmitForm = async (data: any) => {
		try {
			await (isEdit && customField?.id
				? updateCustomFieldMutation.mutateAsync({ campaignId: campaignId || "", id: customField.id, data })
				: createCustomFieldMutation.mutateAsync({ campaignId: campaignId || "", data }));

			onCreateDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;
			if (!Array.isArray(errorMessages)) {
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

	return (
		<React.Fragment>
			<CustomDrawer
				open={isCreateDrawerOpen}
				onClose={onCreateDrawerClose}
				title={isEdit ? "Edit Custom Field" : "Create Custom Field"}
				onSubmit={handleSubmit(onSubmitForm)}
				isSubmitButtonDisabled={
					isSubmitting || createCustomFieldMutation.isPending || updateCustomFieldMutation.isPending
				}
				submitButtonText={isEdit ? "Update Custom Field" : "Create Custom Field"}
        width="420px"
			>
				<FormProvider {...methods}>
					<Grid container spacing={2}>
						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							{" "}
							<Controller
								control={control}
								name="name"
                disabled={isEdit}
								rules={{
									required: "Name is required",
									minLength: {
										value: 2,
										message: "Name must be at least 2 characters",
									},
								}}
								render={({ field, fieldState }) => (
									<FormControl error={Boolean(fieldState.error)} fullWidth required>
										<InputLabel disabled={isEdit}>Name</InputLabel>
										<OutlinedInput {...field} placeholder="Enter name" />
										{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
									</FormControl>
								)}
							/>
						</Grid>

						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							{" "}
							<Controller
								control={control}
								name="label"
								rules={{
									required: "Label is required",
									minLength: {
										value: 2,
										message: "Label must be at least 2 characters",
									},
								}}
								render={({ field, fieldState }) => (
									<FormControl error={Boolean(fieldState.error)} fullWidth required>
										<InputLabel>Label</InputLabel>
										<OutlinedInput {...field} placeholder="Enter label" />
										{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
									</FormControl>
								)}
							/>
						</Grid>

						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							{" "}
							<DynamicField
								control={control}
								type={DynamicFieldType.TEXT_AREA}
								name="description"
								label="Description"
								dynamicField={{
									name: "description",
									type: DynamicFieldType.TEXT_AREA,
									label: "Description",
									placeholder: "Enter description",
									multiline: true,
								}}
							/>
						</Grid>

						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							<DynamicField
								control={control}
								type={DynamicFieldType.BOOLEAN}
								name="enabled"
								label="Enabled"
								dynamicField={{
									name: "enabled",
									type: DynamicFieldType.BOOLEAN,
									label: "Enabled",
								}}
							/>
						</Grid>
					</Grid>
				</FormProvider>
			</CustomDrawer>
		</React.Fragment>
	);
}
